import { useState } from 'react';

const useItemsSelection = <T>() => {
    const [currentSelectedItems, setCurrentSelectedItems] = useState<T[]>([]);

    const getItemId = (item: T) => {
        const itemId = (item as any)?.id;
        return itemId !== undefined && itemId !== null ? itemId : item;
    };

    const isSelected = (item: T) =>
        currentSelectedItems.some((i) => getItemId(i) === getItemId(item));

    const AT_LEAST_ONE_ITEM_SELECTED = currentSelectedItems.length > 0;

    const handleSelectClick = (item: T) => {
        if (isSelected(item)) {
            const itemId = getItemId(item);
            setCurrentSelectedItems((prevSelected) =>
                prevSelected.filter((selectedItem) => getItemId(selectedItem) !== itemId)
            );
        } else {
            setCurrentSelectedItems((prevSelected) => [...prevSelected, item]);
        }
    };

    const handleSelectAllClick = (items: T[], checked?: boolean) => {
        const itemIds = new Set(items.map((item) => getItemId(item)));
        const shouldDeselect = checked === false || (checked === undefined && areAllItemsSelected(items));

        if (shouldDeselect) {
            setCurrentSelectedItems((prevSelected) =>
                prevSelected.filter((selectedItem) => !itemIds.has(getItemId(selectedItem)))
            );
            return;
        }

        setCurrentSelectedItems((prevSelected) => {
            const prevIds = new Set(prevSelected.map((selectedItem) => getItemId(selectedItem)));
            const missingItems = items.filter((item) => !prevIds.has(getItemId(item)));

            return [...prevSelected, ...missingItems];
        });
    };

    const areAllItemsSelected = (items: T[]) =>
        items.length > 0 &&
        items.every((item) => currentSelectedItems.some((i) => getItemId(i) === getItemId(item)));

    const clearAllSelected = () => {
        setCurrentSelectedItems([]);
    };

    return {
        currentSelectedItems,
        isSelected,
        handleSelectClick,
        handleSelectAllClick,
        areAllItemsSelected,
        clearAllSelected,
        AT_LEAST_ONE_ITEM_SELECTED
    };
};

export default useItemsSelection;
